import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {toastr} from "react-redux-toastr";
import {DartsTournaments, ICreateTournamentPayload, IDartsTour, IEditTournamentPayload, ITournamentsInit} from "./TournamentsCRUD.types";
import { API_DARTS_TOUR_PLAYERS, API_DARTS_TOURNAMENT_INIT, API_DARTS_TOURNAMENTS } from "../../services/api/api.urls";
import { api } from "../../services/api/api.service";
import { COMPANY_ABBR } from "../../common/constants/company";
import { TryCatch } from "../../services/TryCatch";
import { IdName } from "../../common/types/IdName";

export interface IGetToursResponse {
    totalItems: number,
    totalPages: number,
    pageNum: number,
    pageSize: number,
    items: IDartsTour[]
}

const initialState: DartsTournaments = {
    dartsTournaments: [],
    editTournament: null,
    editMode: false,
    isVisibleForm: false,
    isLoading: true,
    noContent: null,
    competitionOptions: [],
    groupOptions: [],
    seriesOptions: [],
    pagination: {
        totalPages: 1,
        activePage: 1,
        pageSize: 10
    },
    players: [],
    filter: {
        phase: undefined,
        week: undefined,
        competition: undefined
    }
}

export const getAllTourPlayers = createAsyncThunk<IdName[], void, { rejectValue: string }>(
    'dartsTournaments/get-all-players',
    async (query, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_DARTS_TOUR_PLAYERS}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to fetch darts tournaments.', {id: "get-tour"})
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const getDartsTournaments = createAsyncThunk<IGetToursResponse, string, { rejectValue: string }>(
    'dartsTournaments/get',
    async (query, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_DARTS_TOURNAMENTS}?${query}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to fetch darts tournaments.', {id: "get-tour"})
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const getDartsTournamentsInit = createAsyncThunk<ITournamentsInit, void, { rejectValue: string }>(
    'dartsTournamentsInit/get',
    async (_, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(API_DARTS_TOURNAMENT_INIT).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to fetch darts tournaments init.', {id: "get-init-tour"})
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)


export const createDartsTournament = createAsyncThunk<IDartsTour, ICreateTournamentPayload, { rejectValue: string }>(
    'dartsTournament/post',
    async (tournament, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.post(API_DARTS_TOURNAMENTS, tournament).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to create a tournament.', {id: "error-create-tour"})
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Darts tournament has been created", {id: "succeed-create-tour"});
        return result.data;
    }
)


export const editDartsTournament = createAsyncThunk<IDartsTour, IEditTournamentPayload, { rejectValue: string }>(
    'dartsTournament/edit',
    async (tournament, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.put(`${API_DARTS_TOURNAMENTS}`, tournament).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to edit a tournament.', {id: "error-edit-tour"})
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Darts tournament has been edited", {id: "edit-tour"});
        return result.data;
    }
)

export const deleteDartsTournament = createAsyncThunk<string | undefined, any, { rejectValue: string }>(
    'dartsTournament/delete',
    async (id, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.delete(`${API_DARTS_TOURNAMENTS}/${id}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to delete a darts tournament.', {id: "error-delete-tour"})
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Darts tournament has been deleted", {id: "delete-tour"});
        return result.data;
    }
)


export const TournamentsCRUD = createSlice({name: 'TournamentsCRUD',
    reducers: {
        showTournamentForm: (state) => {
            state.isVisibleForm = true
        },
        hideTournamentForm: (state) => {
            state.isVisibleForm = false;
            state.editMode = false;
        },
        setEditTournamentMode: (state, action: PayloadAction<IDartsTour>) => {
            state.editMode = true;
            state.isVisibleForm = true;
            state.editTournament = action.payload;
        },
        setPageNumber: (state, action) => {
            state.pagination.activePage = action.payload
        },
        changePageSize: (state, action) => {
            state.pagination.pageSize = action.payload
        },
        setFilter: (state, action) => {
            state.filter = {...state.filter, [action.payload.name]: action.payload.value}
        },

        clearFilter: (state) => {
            state.filter = {phase: null, week: null, competition: null}
        },
        setTournamentForScheme: (state, action) => {
            state.editTournament = action.payload
        }

    },
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(getAllTourPlayers.fulfilled, (state, action) => {
                state.players = action.payload
            })
            .addCase(getDartsTournamentsInit.fulfilled, (state, action) => {
                state.competitionOptions = action.payload.competitions
                state.seriesOptions = action.payload.series
                state.groupOptions = action.payload.groups
            })
            .addCase(getDartsTournamentsInit.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(getDartsTournaments.fulfilled, (state, action) => {
                state.dartsTournaments = action.payload.items
                state.isLoading = false;
                state.pagination.totalPages = action.payload.totalPages
            })
            .addCase(getDartsTournaments.rejected, (state) => {
                state.isLoading = false;

            })
            .addCase(deleteDartsTournament.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteDartsTournament.fulfilled, (state, action) => {
                state.isLoading = false;
                state.dartsTournaments = state.dartsTournaments.filter(tour => tour.id !== action.meta.arg.id)
            })
            .addCase(deleteDartsTournament.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(createDartsTournament.pending, (state) => {
                state.isLoading = true;
                state.isVisibleForm = false;
            })
            .addCase(createDartsTournament.fulfilled, (state, action) => {
                state.dartsTournaments.unshift(action.payload);
                state.isLoading = false;
            })
            .addCase(createDartsTournament.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(editDartsTournament.pending, (state) => {
                state.isVisibleForm = false;
                state.isLoading = true;
            })
            .addCase(editDartsTournament.fulfilled, (state, action) => {
                state.dartsTournaments = state.dartsTournaments.map((tournament) => {
                    if (tournament.id === action.payload.id) {
                        tournament = action.payload
                    }
                    return tournament
                })
                state.editTournament = null;
                state.isLoading = false;
            })
            .addCase(editDartsTournament.rejected, (state) => {
                state.editTournament = null;
                state.isLoading = false;
            });
    },
});

export const {
    showTournamentForm,
    hideTournamentForm,
    setEditTournamentMode,
    setPageNumber,
    setFilter,
    clearFilter,
    changePageSize,
    setTournamentForScheme
} = TournamentsCRUD.actions
export default TournamentsCRUD.reducer;

